import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {getNoOfGridItems, reloadGrid} from '@Components/base-grid';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import {type AiChatGeneratedAsset} from '@AiChat/ai-chat.types';
import {getGeneratedAssets} from '@AiChat/lib/ai-chat-assets-library';
import {openErrorModal} from '@Modals/error-modal';
import {noop} from '@Utils/general.util';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {AiChatAssetsGridTile} from '../ai-chat-assets-grid-tile/ai-chat-assets-grid-tile';
import {AiChatAssetsEmptyState} from '../ai-chat-assets-empty-state/ai-chat-assets-empty-state';
import type {AiChatAssetsGridProps} from './ai-chat-assets-grid.types';
import {
  AI_CHAT_ASSETS_GRID_ID,
  ASSETS_GRID_BATCH_SIZE,
  ASSETS_GRID_ITEM_GAP,
  ASSETS_GRID_ITEM_GAP_MOBILE,
  ASSETS_GRID_ITEM_HEIGHT,
  ASSETS_GRID_ITEM_HEIGHT_MOBILE,
} from './ai-chat-assets-grid.types';

export function AiChatAssetsGrid({filter, scrollingContainer, onAssetClick}: AiChatAssetsGridProps): ReactElement {
  const {isSmall} = useAiChatSize();
  const dispatch = useAppDispatch();
  const hasMountedRef = useRef(false);
  const seenAssetIds = useRef<Set<string>>(new Set());
  const [hasLoadedFirstBatch, setHasLoadedFirstBatch] = useState(false);
  const noOfGridItems = useAppSelector((reduxState) => {
    return getNoOfGridItems(reduxState, AI_CHAT_ASSETS_GRID_ID);
  });

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setHasLoadedFirstBatch(false);
    seenAssetIds.current = new Set();
    dispatch(reloadGrid({gridId: AI_CHAT_ASSETS_GRID_ID, gridSelectionGroupId: AI_CHAT_ASSETS_GRID_ID}));
  }, [filter, dispatch]);

  const getGridItem = (asset: AiChatGeneratedAsset): GridItemStorage => {
    return {
      type: GRID_ITEM_TYPE.CUSTOM,
      id: asset.assetHashedFilename,
      width: asset.width ?? undefined,
      height: asset.height ?? undefined,
      children: (
        <AiChatAssetsGridTile
          key={asset.assetHashedFilename}
          asset={asset}
          onClick={(): void => {
            onAssetClick(asset);
          }}
        />
      ),
    };
  };

  const getItems = async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    const offset = Object.values(params.offsets).reduce((agg: number, value: number) => agg + value, 0);
    let assets: AiChatGeneratedAsset[] = [];

    try {
      assets = await getGeneratedAssets(filter, params.limit, offset);
    } catch (error) {
      console.error(error);
      openErrorModal();
    }

    return {
      response: assets.map(getGridItem),
      offsetsData: {DEFAULT_SOURCE: Math.min(assets.length, ASSETS_GRID_BATCH_SIZE)},
    };
  };

  const removeAssetsAlreadyLoaded = (items: GridItemStorage[]): GridItemStorage[] => {
    return items.filter((item) => {
      if (seenAssetIds.current.has(item.id)) {
        return false;
      }

      seenAssetIds.current.add(item.id);
      return true;
    });
  };

  const renderEmptyState = (): ReactElement | null => {
    if (!hasLoadedFirstBatch || noOfGridItems > 0) {
      return null;
    }

    return <AiChatAssetsEmptyState filter={filter} />;
  };

  const getGridItemHeight = (): number => {
    if (isSmall) {
      return ASSETS_GRID_ITEM_HEIGHT_MOBILE;
    }

    return ASSETS_GRID_ITEM_HEIGHT;
  };

  const getGridItemsGap = (): number => {
    if (isSmall) {
      return ASSETS_GRID_ITEM_GAP_MOBILE;
    }

    return ASSETS_GRID_ITEM_GAP;
  };

  const renderGrid = (): ReactElement => {
    return (
      <LazyMasonryGrid
        gridId={AI_CHAT_ASSETS_GRID_ID}
        gridSelectionGroupId={AI_CHAT_ASSETS_GRID_ID}
        getItems={getItems}
        transformResponse={removeAssetsAlreadyLoaded}
        defaultHeight={getGridItemHeight()}
        itemsGap={getGridItemsGap()}
        loadItemBatchSize={ASSETS_GRID_BATCH_SIZE}
        customOnGridItemClicked={noop}
        showHoverOverlay={false}
        showSelectionOverlay={false}
        makeContentScrollable={false}
        clearGridOnUnmount
        initialItemsBatchLoaded={(): void => {
          setHasLoadedFirstBatch(true);
        }}
        scrollingContainer={scrollingContainer}
        className="_full-width"
      />
    );
  };

  return (
    <>
      {renderGrid()}
      {renderEmptyState()}
    </>
  );
}
